import { useState } from 'react';

import clsx from 'clsx';
import { Plus, Minus } from 'lucide-react';

interface Option {
  value: string;
  label: string;
}

interface DualSelectProps {
  label: string;
  options: Option[];
  selected: Option[];
  onChange: (selected: Option[]) => void;
  errorMessage?: string;
}

export function DualSelect({
  label,
  options,
  selected,
  onChange,
  errorMessage,
}: DualSelectProps): JSX.Element {
  const handleAdd = (option: Option) => {
    if (!selected.find(s => s.value === option.value)) {
      onChange([...selected, option]);
    }
  };

  const handleRemove = (option: Option) => {
    onChange(selected.filter(s => s.value !== option.value));
  };

  const available = options.filter(
    o => !selected.find(s => s.value === o.value),
  );

  return (
    <fieldset className="flex flex-col gap-2 w-full notranslate">
      <label className="text-high-emphasis">{label}</label>

      <div
        className={clsx(
          'gap-4 p-3 border rounded-lg',
          errorMessage ? 'border-destructive-main' : 'border-neutral-lighter',
        )}
      >
        {/* Selecionados */}
        <div className="flex-1">
          <span className="block text-sm font-medium text-primary-main mb-2">
            Selecionados
          </span>
          <ul className="flex flex-col gap-2">
            {selected.length === 0 && (
              <li className="text-sm text-neutral-500">Nenhum selecionado</li>
            )}
            {selected.map(opt => (
              <li
                key={opt.value}
                className="flex items-center justify-between bg-primary-light/10 px-3 py-2 rounded-lg"
              >
                <span className="text-sm">{opt.label}</span>
                <button
                  type="button"
                  onClick={() => handleRemove(opt)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Minus size={16} />
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Disponíveis */}
        <div className="flex-1">
          <span className="block text-sm font-medium text-primary-main mb-2">
            Disponíveis
          </span>
          <ul className="flex flex-col gap-2 max-h-48 overflow-y-auto">
            {available.length === 0 && (
              <li className="text-sm text-neutral-500">Todos selecionados</li>
            )}
            {available.map(opt => (
              <li
                key={opt.value}
                className="flex items-center justify-between hover:bg-neutral-100 px-3 py-2 rounded-lg"
              >
                <span className="text-sm">{opt.label}</span>
                <button
                  type="button"
                  onClick={() => handleAdd(opt)}
                  className="text-green-500 hover:text-green-700"
                >
                  <Plus size={16} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {errorMessage && (
        <span className="text-xs text-destructive-dark mt-1">
          {errorMessage}
        </span>
      )}
    </fieldset>
  );
}
